import React, { useEffect, useState, useRef } from 'react';
import { Select, Button, Row, Col, Spin, DatePicker, message } from 'antd';
import styled from 'styled-components';
import { restApi } from '../../../constants/api';
import { PrinterOutlined, RedoOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;

// (kept) original global style (not used in the table render)
const cellStyleGlobal: React.CSSProperties = {
  padding: '10px',
  border: '1px solid #ccc',
  textAlign: 'center',
  verticalAlign: 'middle',
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  wordBreak: 'break-word',
};

type CellMeta = {
  user_id: number | undefined;
  cycle_id: number | undefined;
  attendance_id: number | undefined;
  statusLetter: 'P' | 'A' | 'L' | string;
  statusFull: 'PRESENT' | 'ABSENT' | 'LEAVE' | string;
};

const STATUS_LETTER_TO_FULL: Record<string, 'PRESENT' | 'ABSENT' | 'LEAVE' | string> = {
  P: 'PRESENT',
  A: 'ABSENT',
  L: 'LEAVE',
};
const STATUS_FULL_TO_LETTER: Record<string, 'P' | 'A' | 'L' | string> = {
  PRESENT: 'P',
  ABSENT: 'A',
  LEAVE: 'L',
};

const getStatusColor = (letter?: string) => {
  if (letter === 'P') return 'green';
  if (letter === 'A') return 'red';
  if (letter === 'L') return '#d48806'; // warm amber
  return 'inherit';
};

const AttendanceList = () => {
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [selected, setSelected] = useState({
    department: undefined as number | undefined,
    designation: undefined as number | undefined,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
  });
  const printRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState<any>([]);

  // track which cell is being edited (user_id + date)
  const [editingCell, setEditingCell] = useState<{ user_id: number; date: string } | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // UI override: what to show after update (no page refresh)
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, 'P' | 'A' | 'L'>
  >({});

  // ✅ NEW: remember insertId locally so next edit becomes UPDATE (PATCH)
  const [attendanceIdOverrides, setAttendanceIdOverrides] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [deptRes, desigRes]: any = await Promise.all([
          restApi('/api/departments', 'GET'),
          restApi('/api/user-types', 'GET'),
        ]);
        setDepartments(deptRes?.data || []);
        setDesignations(desigRes?.data || []);
      } catch (error) {
        console.error('Error fetching filters:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, []);

  const handleSearch = async () => {
    if (!selected.startDate || !selected.endDate) {
      alert('Please select start and end date.');
      return;
    }

    setDataLoading(true);
    try {
      const res: any = await restApi('/api/reports/attendance-status', 'POST', {
        department_id: selected.department,
        user_type_id: selected.designation,
        start_date: selected.startDate,
        end_date: selected.endDate,
      });
      setAttendanceData(res?.data || []);
      // Whenever fresh data is loaded, clear overrides
      setStatusOverrides({});
      setAttendanceIdOverrides({}); // ✅ clear ids too
    } catch (err) {
      console.error('Failed to fetch attendance data', err);
    } finally {
      setDataLoading(false);
    }
  };

  // (kept) local cell style used in your table
  const cellStyle: React.CSSProperties = {
    border: '1px solid #ddd',
    padding: 8,
    textAlign: 'center',
    fontSize: 14,
  };

  const handleReset = () => {
    setSelected({
      department: undefined,
      designation: undefined,
      startDate: undefined,
      endDate: undefined,
    });
    setAttendanceData([]);
    setStatusOverrides({});
    setAttendanceIdOverrides({}); // ✅ clear ids too
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printContent = printRef.current.innerHTML;
      const win = window.open('', '', 'width=900,height=700');
      if (win) {
        win.document.write(`
          <html>
            <head>
              <title>Attendance Report</title>
              <style>
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 14px; }
                th { background-color: #76d5c8; }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
      }
    }
  };

  const groupByDepartment = (data: any[]) => {
    const grouped: any = {};
    data.forEach((emp) => {
      if (!grouped[emp.department_name]) {
        grouped[emp.department_name] = [];
      }
      grouped[emp.department_name].push(emp);
    });
    return grouped;
  };

  const groupedData = groupByDepartment(attendanceData);

  // Robust parser: supports
  //   "YYYY-MM-DD:P"
  //   "YYYY-MM-DD:P:USER_ID:CYCLE_ID:ATTENDANCE_ID"
  const buildAttendanceMap = (raw: string, fallbackUserId?: number, fallbackCycleId?: number) => {
    const acc: Record<
      string,
      CellMeta & {
        _raw: string;
      }
    > = {};

    if (!raw) return acc;

    (raw.split(', ') || []).forEach((chunk) => {
      const parts = chunk.split(':'); // [date, status, user_id, cycle_id, attendance_id]
      const date = parts[0];
      const statusToken = parts[1];

      if (!date || !statusToken) return;

      // normalize status
      const letter =
        statusToken === 'PRESENT' || statusToken === 'ABSENT' || statusToken === 'LEAVE'
          ? (STATUS_FULL_TO_LETTER[statusToken] as 'P' | 'A' | 'L')
          : (statusToken as 'P' | 'A' | 'L');

      const full =
        statusToken === 'P' || statusToken === 'A' || statusToken === 'L'
          ? (STATUS_LETTER_TO_FULL[statusToken] as 'PRESENT' | 'ABSENT' | 'LEAVE')
          : (statusToken as 'PRESENT' | 'ABSENT' | 'LEAVE');

      const user_id = parts[2] ? Number(parts[2]) : fallbackUserId;
      const cycle_id = parts[3] ? Number(parts[3]) : fallbackCycleId;
      const attendance_id = parts[4] ? Number(parts[4]) : 0;

      acc[date] = {
        _raw: chunk,
        user_id,
        cycle_id,
        attendance_id,
        statusLetter: letter,
        statusFull: full,
      };
    });

    return acc;
  };

  const handleStatusChange = async (
    newStatusFull: 'PRESENT' | 'ABSENT' | 'LEAVE',
    meta: { user_id?: number; attendance_date: string; cycle_id?: number; attendance_id?: number }
  ) => {
    if (!meta.user_id || !meta.cycle_id) {
      message.error('Some this is Missing for this attendance row. Please to company person!');
      return;
    }

    // ✅ compute effective attendance_id from overrides first
    const key = `${meta.user_id}-${meta.attendance_date}`;
    const effectiveAttendanceId =
      attendanceIdOverrides[key] ?? meta.attendance_id ?? 0;

    setSaving(true);
    try {
      const currentDate = new Date();
      const attendanceDate = new Date(meta.attendance_date);

      if (attendanceDate <= currentDate) {
        // --- API CALLS (same as before, but NO handleSearch) ---

        if (effectiveAttendanceId && effectiveAttendanceId !== 0) {
          // UPDATE
          if (localStorage.getItem('role') === 'SUPERADMIN') {
            await restApi('/api/attendance', 'PATCH', {
              attendance_id: effectiveAttendanceId,
              status: newStatusFull,
            });
          } else {
            message.error(`You don't have access to update attendance!`);
            return;
          }
        } else {
          // CREATE
          if (!meta.cycle_id) {
            message.error('Missing cycle_id to create attendance.');
            return;
          }
          const result: any = await restApi('/api/attendance', 'POST', {
            user_id: meta.user_id,
            attendance_date: meta.attendance_date,
            status: newStatusFull,
            cycle_id: meta.cycle_id,
          });

          // ✅ capture insert id and store locally
          const insertedId =
            Number(result?.data?.insertId ?? result?.data?.id ?? 0);

          if (insertedId > 0) {
            setAttendanceIdOverrides((prev) => ({
              ...prev,
              [key]: insertedId,
            }));
          }

          // console.log("---------result line 269", result);
          // console.log("--------- insert id is ", insertedId);
        }

        // UI OVERRIDE: change value immediately in table (no refresh)
        const newLetter = STATUS_FULL_TO_LETTER[newStatusFull] as 'P' | 'A' | 'L';
        setStatusOverrides((prev) => ({
          ...prev,
          [key]: newLetter,
        }));

        message.success('Attendance updated.');
        setEditingCell(null);

        // ❌ NO await handleSearch(); here
      } else {
        message.error(`Don't update attendance of future dates.`);
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to update attendance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FilterContainer>
      {loading ? (
        <Spin size="large" />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {localStorage.getItem('role') === 'SUPERADMIN' && (
              <Col xs={24} md={6}>
                <Select
                  placeholder="Select Department"
                  value={selected.department as any}
                  onChange={(value) => setSelected((prev) => ({ ...prev, department: value }))}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {departments.map((d: any) => (
                    <Option key={d.id} value={d.id}>
                      {d.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}

            <Col xs={24} md={6}>
              <Select
                placeholder="Select Designation"
                value={selected.designation as any}
                onChange={(value) => setSelected((prev) => ({ ...prev, designation: value }))}
                style={{ width: '100%' }}
                allowClear
              >
                {designations.map((d: any) => (
                  <Option key={d.id} value={d.id}>
                    {d.type_name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={(dates) => {
                  setSelected((prev: any) => ({
                    ...prev,
                    startDate: dates?.[0]?.format('YYYY-MM-DD'),
                    endDate: dates?.[1]?.format('YYYY-MM-DD'),
                  }));
                }}
                value={
                  selected.startDate && selected.endDate
                    ? [dayjs(selected.startDate), dayjs(selected.endDate)]
                    : null
                }
              />
            </Col>
            <Col xs={24} md={6}></Col>
            <Col xs={24} md={24}>
              <Button
                style={{ background: '#77d6c9', color: 'black' }}
                icon={<SearchOutlined style={{ color: 'white', fontSize: '17px' }} />}
                onClick={handleSearch}
              >
                Search
              </Button>{' '}
              &nbsp;
              <Button
                style={{ background: '#77d6c9', color: 'black' }}
                icon={<RedoOutlined style={{ color: 'white', fontSize: '17px' }} />}
                onClick={handleReset}
              >
                Reset
              </Button>
              &nbsp;&nbsp;
              <Button
                style={{ background: '#77d6c9', color: 'black' }}
                icon={<PrinterOutlined style={{ color: 'white', fontSize: '17px' }} />}
                onClick={handlePrint}
              >
                Print Report
              </Button>
            </Col>
          </Row>

          {dataLoading ? (
            <Spin style={{ marginTop: 20 }} />
          ) : (
            attendanceData.length > 0 && (
              <div style={{ marginTop: 32 }} ref={printRef}>
                <h3>
                  Attendance Report (From:{' '}
                  {selected.startDate ? dayjs(selected.startDate).format('DD-MMMM YYYY') : ''}{' '}
                  &nbsp;To:{' '}
                  {selected.endDate ? dayjs(selected.endDate).format('DD-MMMM YYYY') : ''})
                </h3>
                <StickyTableWrapper>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginTop: 20,
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    <thead style={{ backgroundColor: '#76d5c8', color: '#000' }}>
                      <tr>
                        <th
                          style={{
                            ...cellStyle,
                            textAlign: 'left',
                            minWidth: '200px',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          Name
                        </th>
                        <th style={cellStyle}>Designation</th>
                        {attendanceData[0]?.attendance_by_day
                          ?.split(', ')
                          ?.map((entry: string) => {
                            const [date] = entry.split(':');
                            const formatted = dayjs(date).format('D MMM');
                            return (
                              <th key={date} style={cellStyle}>
                                {formatted}
                              </th>
                            );
                          })}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(groupedData).map(([department, deptRows]: any, deptIndex) => (
                        <React.Fragment key={`dept-wrap-${deptIndex}`}>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <td
                              colSpan={32}
                              style={{
                                fontWeight: 'bold',
                                padding: '12px',
                                textAlign: 'center',
                                border: '1px solid #ddd',
                              }}
                            >
                              {department}
                            </td>
                          </tr>
                          {deptRows.map((user: any, index: number) => {
                            const attendanceMap = buildAttendanceMap(
                              user.attendance_by_day,
                              user.user_id,
                              user.cycle_id
                            );

                            return (
                              <tr
                                key={`${department}-${user.user_id || index}`}
                                style={{
                                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                                  transition: 'background-color 0.3s',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor = '#e0f7fa')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    index % 2 === 0 ? '#ffffff' : '#f9f9f9')
                                }
                              >
                                <td style={cellStyle}>
                                  {user.prefixName}. {user.user_name}
                                </td>
                                <td style={cellStyle}>{user.designation}</td>
                                {Object.keys(attendanceMap).map((date) => {
                                  const meta = attendanceMap[date];

                                  const key = `${meta.user_id ?? user.user_id}-${date}`;
                                  const overrideLetter = statusOverrides[key];
                                  const displayLetter = overrideLetter ?? meta?.statusLetter;

                                  // ✅ effective attendance id for later PATCH
                                  const effectiveAttendanceId =
                                    attendanceIdOverrides[key] ??
                                    meta.attendance_id ??
                                    0;

                                  const isEditing =
                                    editingCell?.user_id === (meta.user_id ?? user.user_id) &&
                                    editingCell?.date === date;

                                  // value for Select when editing (respect override if exists)
                                  const selectValue = overrideLetter
                                    ? (STATUS_LETTER_TO_FULL[
                                        overrideLetter
                                      ] as 'PRESENT' | 'ABSENT' | 'LEAVE')
                                    : ((meta?.statusFull as
                                        | 'PRESENT'
                                        | 'ABSENT'
                                        | 'LEAVE') ||
                                        (STATUS_LETTER_TO_FULL[
                                          meta?.statusLetter
                                        ] as 'PRESENT' | 'ABSENT' | 'LEAVE'));

                                  return (
                                    <td
                                      key={`${user.user_id}-${date}`}
                                      style={{
                                        ...cellStyle,
                                        color: getStatusColor(displayLetter),
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        position: 'relative',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (saving) return;
                                        setEditingCell({
                                          user_id: meta.user_id ?? user.user_id,
                                          date,
                                        });
                                      }}
                                    >
                                      {isEditing ? (
                                        <Select
                                          size="small"
                                          value={selectValue}
                                          style={{ minWidth: 140 }}
                                          open
                                          autoFocus
                                          onBlur={() => !saving && setEditingCell(null)}
                                          onChange={(newVal) =>
                                            handleStatusChange(
                                              newVal as 'PRESENT' | 'ABSENT' | 'LEAVE',
                                              {
                                                user_id: meta.user_id ?? user.user_id,
                                                attendance_date: date,
                                                cycle_id: meta.cycle_id ?? user.cycle_id,
                                                attendance_id: effectiveAttendanceId, // ✅ pass effective id
                                              }
                                            )
                                          }
                                          dropdownMatchSelectWidth={false}
                                          options={[
                                            { label: 'P - PRESENT', value: 'PRESENT' },
                                            { label: 'A - ABSENT', value: 'ABSENT' },
                                            { label: 'L - LEAVE', value: 'LEAVE' },
                                          ]}
                                        />
                                      ) : (
                                        displayLetter
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </StickyTableWrapper>
              </div>
            )
          )}
        </>
      )}
    </FilterContainer>
  );
};

export default AttendanceList;

const StickyTableWrapper = styled.div`
  /* scroll container */
  max-height: 70vh;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  -webkit-overflow-scrolling: touch;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;                 /* keep above rows */
    background: #76d5c8;        /* your header color */
    /* Optional: subtle separator under sticky header */
    box-shadow: 0 2px 0 rgba(0,0,0,0.04);
  }
`;
